import React, { createContext, useCallback, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { App } from 'antd'
import { useLocalDb } from './useLocalDb'
import useInterval from './useInterval'

export type httpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

interface AccountContextType {
    authenticate: Function
    logOut: Function
    authenticatedFetch: Function
    authenticatedFetchImg: Function
    getAccessToken: Function
    hasRefreshToken: Function
    getTipoUsuario: Function
    getNomeUsuario: Function
    getEmailUsuario: Function
}

export const AccountContext = createContext<AccountContextType>({
    authenticate: (username: string, password: string, lembrar: boolean) => {
    },
    logOut: () => {
    },
    authenticatedFetch: () => {
    },
    authenticatedFetchImg: () => {
    },
    getAccessToken: () => {
    },
    hasRefreshToken: () => {
    },
    getTipoUsuario: () => {
    },
    getNomeUsuario: () => {
    },
    getEmailUsuario: () => {
    },
})

// Explicitly use the /api proxy for Cloudflare Functions
const API_BASE = '/api'

const Auth = (props: React.PropsWithChildren) => {
    const { message } = App.useApp()
    const { isOnline } = useLocalDb()
    const navigate = useNavigate()
    const refreshTokenRef = useRef(false)

    const getCookie = useCallback((name: string) => {
        const value = `; ${document.cookie}`
        const parts: Array<any> = value.split(`; ${name}=`)
        if (parts.length === 2) {
            return parts.pop().split(';').shift()
        } else {
            return null
        }
    }, [])

    const getAccessToken = useCallback(() => getCookie('accessToken'), [getCookie])
    const getRefreshToken = useCallback(() => getCookie('refreshToken'), [getCookie])
    const getTipoUsuario = useCallback(() => getCookie('tipoUsuario'), [getCookie])
    const getNomeUsuario = useCallback(() => getCookie('nomeUsuario'), [getCookie])
    const getEmailUsuario = useCallback(() => getCookie('emailUsuario'), [getCookie])

    const hasRefreshToken = useCallback(() => {
        const token = getRefreshToken()
        return token !== null && token !== ''
    }, [getRefreshToken])

    const setCookiesInBrowser = useCallback((
        accessToken: String,
        accessTokenExpiresIn: number,
        refreshToken: String,
        refreshTokenExpiresIn: number,
        tipoUsuario: String,
        nome: String,
        email: String,
    ) => {
        const cookieDefaults = '; Path=/; SameSite=Lax; max-age='
        document.cookie = `accessToken=${accessToken}${cookieDefaults}${accessTokenExpiresIn / 1000}`
        document.cookie = `refreshToken=${refreshToken}${cookieDefaults}${refreshTokenExpiresIn / 1000}`
        document.cookie = `tipoUsuario=${tipoUsuario}${cookieDefaults}${refreshTokenExpiresIn / 1000}`
        document.cookie = `nomeUsuario=${nome}${cookieDefaults}${refreshTokenExpiresIn / 1000}`
        document.cookie = `emailUsuario=${email}${cookieDefaults}${refreshTokenExpiresIn / 1000}`
    }, [])

    const logOut = useCallback(() => {
        document.cookie = 'accessToken=; Path=/; max-age=0;'
        document.cookie = 'refreshToken=; Path=/; max-age=0;'
        document.cookie = 'tipoUsuario=; Path=/; max-age=0;'
        document.cookie = 'nomeUsuario=; Path=/; max-age=0;'
        document.cookie = 'emailUsuario=; Path=/; max-age=0;'

        const publicRoutes = [
            '/login',
            '/cadastro',
            '/recuperar-senha',
            '/recuperarsenha',
            '/resetar-senha',
            '/resetarsenha',
            '/',
        ]
        if (!publicRoutes.includes(window.location.pathname)) {
            navigate('/login')
        }
    }, [navigate])

    const refreshToken = useCallback(async () => {
        return await fetch(
            API_BASE + '/auth/refreshToken',
            {
                method: 'POST',
                headers: new Headers({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ refreshToken: getRefreshToken() }),
            }
        )
            .then((res) => {
                if (res && res.status === 200) {
                    return res.json().then((data) => {
                        setCookiesInBrowser(
                            data.accessToken,
                            data.accessTokenExpiresIn,
                            data.refreshToken,
                            data.refreshTokenExpiresIn,
                            data.tipoUsuario,
                            data.nome,
                            data.email
                        )
                    })
                } else {
                    message.error({
                        content: (
                            <>
                                Erro ao tentar atualizar os tokens, faça o login
                                novamente.
                            </>
                        ),
                        duration: 6,
                        key: 'refreshTokenFetchError',
                    })
                    logOut()
                }
            })
            .catch((err) => {
                console.error('Refresh Token Error Details:', err)
                if (isOnline && hasRefreshToken()) {
                    message.error({
                        key: 'refreshTokenFetchError',
                        content:
                            'Erro ao contactar o servidor: ' + (err.message || 'Erro de Rede'),
                        duration: 10,
                    })
                }
            })
    }, [isOnline, hasRefreshToken, getRefreshToken, logOut, message, setCookiesInBrowser])

    useEffect(() => {
        if (hasRefreshToken() && isOnline && !refreshTokenRef.current) {
            console.log('Napi Solar App - Backend via Cloudflare Function Proxy')
            refreshToken()
        }

        return () => {
            refreshTokenRef.current = true
        }
    }, [isOnline, hasRefreshToken, refreshToken])

    useInterval(async () => {
        if (hasRefreshToken() && isOnline) {
            await refreshToken()
        }
    }, 60 * 1000 * 14) // Roda a cada 14 minutos

    const authenticate = useCallback((
        username: string,
        password: string,
        lembrar: boolean,
    ) => {
        const url = API_BASE + '/auth/login'
        console.log('Fetching:', url)
        fetch(url, {
            method: 'POST',
            headers: new Headers({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
                email: username,
                senha: password,
                lembrar: lembrar,
            }),
        })
            .then((res) => {
                if (res && res.status === 200) {
                    return res.json().then((data) => {
                        setCookiesInBrowser(
                            data.accessToken,
                            data.accessTokenExpiresIn,
                            data.refreshToken,
                            data.refreshTokenExpiresIn,
                            data.tipoUsuario,
                            data.nome,
                            data.email
                        )
                        navigate('/app/dashboard')
                    })
                } else if (res && res.status === 403) {
                    message.warning(
                        <>
                            Sua conta não está ativa.
                            <br />É necessário solicitar a ativação da sua conta
                            com o administrador para poder fazer o login.
                        </>,
                        6
                    )
                } else {
                    message.error(
                        <>
                            Erro ao tentar fazer o login, verifique suas
                            credenciais e tente novamente
                        </>,
                        6
                    )
                }
            })
            .catch((err) => {
                console.error('Login Error Details:', err)
                message.error(
                    'Erro ao contactar o servidor: ' + (err.message || 'Erro de Rede') + ' URL: ' + url,
                    12
                )
            })
    }, [message, navigate, setCookiesInBrowser])

    const authenticatedFetch = useCallback(async (
        url: string,
        method: httpMethod,
        body: string,
    ) => {
        let accessToken = getAccessToken()

        // Ensure we use the proxy base
        const fullUrl = url.startsWith('http') ? url : API_BASE + (url.startsWith('/') ? url : '/' + url)

        if (accessToken === null && !hasRefreshToken()) {
            message.warning({
                key: 'sessionExpired',
                content: 'Sua sessão expirou, faça o login novamente',
                duration: 6,
            })
            logOut()
        }

        return fetch(fullUrl, {
            method: method,
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + accessToken,
            }),
            body: body,
        })
    }, [getAccessToken, hasRefreshToken, logOut, message])

    const authenticatedFetchImg = useCallback(async (
        url: string,
        method: httpMethod,
        body?: BodyInit | null,
        additionalHeaders: Record<string, string> = {},
    ) => {
        let accessToken = getAccessToken()

        // Ensure we use the proxy base
        const fullUrl = url.startsWith('http') ? url : API_BASE + (url.startsWith('/') ? url : '/' + url)

        if (accessToken === null && !hasRefreshToken()) {
            message.warning({
                key: 'sessionExpired',
                content: 'Sua sessão expirou, faça o login novamente',
                duration: 6,
            })
            logOut()
            throw new Error('Sessão expirada')
        }

        const headers = new Headers(additionalHeaders)
        headers.append('Authorization', `Bearer ${accessToken}`)

        if (method === 'GET' && !body) {
            headers.delete('Content-Type')
        }

        return fetch(fullUrl, {
            method,
            headers,
            body: method !== 'GET' ? body : null, // Ignorar body em GET
        })
    }, [getAccessToken, hasRefreshToken, logOut, message])

    return (
        <AccountContext.Provider
            value={{
                authenticate,
                logOut,
                authenticatedFetch,
                authenticatedFetchImg,
                getAccessToken,
                hasRefreshToken,
                getTipoUsuario,
                getNomeUsuario,
                getEmailUsuario,
            }}
        >
            {props.children}
        </AccountContext.Provider>
    )
}

export default Auth
